import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import Grid from '@mui/material/Grid'
import ReactMarkdown from 'react-markdown'
import { H2, Text1 } from 'ui/shorthands/Texts'
import type { PharmacyProps } from 'types/common'
import Link from '@mui/material/Link'

const useStyles = makeStyles()((theme: Theme) => ({
  header: {
    padding: theme.spacing(2, 2, 0, 2),
    display: 'flex',
    alignContent: 'center',
  },
  delivery: {
    fontSize: 12,
    lineHeight: 1.5,
    marginLeft: theme.spacing(2),
    borderRadius: 4,
    background: theme.palette.background.default,
    padding: theme.spacing(0.75, 1),
  },
  content: {
    padding: theme.spacing(1, 2, 2),
  },
  inLine: {
    paddingLeft: 0,
  },
  item: {
    fontSize: theme.typography.fontSize,
    display: 'flex',
    alignContent: 'top',
    padding: theme.spacing(0.75, 0),
  },
  itemLabel: {
    color: theme.palette.text.secondary,
    width: '5em',
  },
  itemContent: {
    whiteSpace: 'pre-wrap',
  },
  button: {
    alignSelf: 'right',
    paddingLeft: theme.spacing(1),
    marginLeft: 'auto',
    marginBottom: theme.spacing(-3), // NOTE: ボタンが下にはみ出てもheaderレイアウトが崩れないように
  },
}))

const InfoItem: React.FC<{
  label: string
  half?: boolean
  children: React.ReactNode
}> = ({ label, half, children }) => {
  const { classes } = useStyles()
  const content = (
    <div className={classes.item}>
      <div className={classes.itemLabel}>{label}</div>
      <div className={classes.itemContent}>{children}</div>
    </div>
  )
  return half ? (
    <Grid container item xs={6}>
      {content}
    </Grid>
  ) : (
    content
  )
}

export type PharmacyInfoProps = {
  type?: 'med' | 'personal' | 'personal_todokusuri'
  inLine?: boolean
  showDetail?: boolean
  pharmacy: PharmacyProps
  button?: React.ReactNode
}

const PharmacyInfo: React.FC<PharmacyInfoProps> = ({
  type = 'med',
  inLine = false,
  showDetail = false,
  pharmacy,
  button,
}) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()

  const Description: React.FC<{ children: string }> = ({ children }) => (
    <Text1>
      <ReactMarkdown
        allowedElements={['text', 'strong', 'a']}
        unwrapDisallowed={true}
        components={{
          a: (props) => (
            <Link href={props.href} target="_blank" rel="noopener noreferrer">
              {props.children}
            </Link>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </Text1>
  )

  return (
    <div>
      <div className={cx(classes.header, inLine && classes.inLine)}>
        <H2>
          <Text1>{pharmacy.name}</Text1>
        </H2>
        {pharmacy.is_possible_delivery && (
          <span className={classes.delivery}>
            {type === 'med' ? t('オンライン服薬指導対応') : t('配送対応')}
          </span>
        )}
        {button && <div className={classes.button}>{button}</div>}
      </div>
      <div className={cx(classes.content, inLine && classes.inLine)}>
        <Grid>
          {pharmacy.address && (
            <InfoItem label={t('住所')}>{pharmacy.address}</InfoItem>
          )}
        </Grid>
        <Grid container>
          {type === 'med'
            ? pharmacy.phone_for_institution && (
                <InfoItem half label={t('電話')}>
                  {pharmacy.phone_for_institution}
                </InfoItem>
              )
            : pharmacy.phone_for_patient && (
                <InfoItem half label={t('電話')}>
                  {pharmacy.phone_for_patient}
                </InfoItem>
              )}
          {type === 'med' && pharmacy.fax && (
            <InfoItem half label={t('FAX')}>
              {pharmacy.fax}
            </InfoItem>
          )}
        </Grid>
        <Grid container>
          {pharmacy.business_day && (
            <InfoItem half label={t('営業時間')}>
              {pharmacy.business_day}
            </InfoItem>
          )}
          {pharmacy.close_day && (
            <InfoItem half label={t('定休日')}>
              {pharmacy.close_day}
            </InfoItem>
          )}
        </Grid>
        {showDetail && (
          <>
            {pharmacy.url && (
              <Grid>
                <InfoItem label={t('URL')}>
                  <Link
                    href={pharmacy.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {pharmacy.url}
                  </Link>
                </InfoItem>
              </Grid>
            )}
            {type.startsWith('personal') && pharmacy.description_for_patient && (
              <Grid>
                <InfoItem label={t('連絡事項')}>
                  <Description>{pharmacy.description_for_patient}</Description>
                </InfoItem>
              </Grid>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default PharmacyInfo
